import {HttpClient, HttpParams} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {MatPaginator} from '@angular/material/paginator';
import {MatSort} from '@angular/material/sort';
import {BankAccount} from '~local/app/types/BankAccount';
import {User, UserResponse} from '~local/app/types/User';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {DataSourceResponse, DataSourceService} from './DataSourceService';
import {UserRoleDto} from '~shared/types/user.types';
import {TransactionMonitoring} from '~shared/types/transaction-types';
import {UpdateProfileByAdminType} from '~shared/types/form-types';

@Injectable()
export class UsersService extends DataSourceService<User> {
    private readonly httpClient = inject(HttpClient);

    public loadData(
        filter: string = '',
        orderBy: string = '',
        orderDirection: string = 'ASC',
        pageNumber: number = 0,
        pageSize: number = 10,
        globalFilter: string = null
    ): Observable<DataSourceResponse<User>> {
        return this.httpClient.get<DataSourceResponse<User>>('/admin/users/list/' + (globalFilter ? globalFilter : ''), {
            params: new HttpParams()
                .set('filter', filter)
                .set('orderBy', orderBy)
                .set('sort', orderDirection.toUpperCase())
                .set('skip', pageNumber.toString())
                .set('take', pageSize.toString())
        });
    }

    public getUsers(
        filter: string = '',
        orderBy: string = '',
        orderDirection: string = 'ASC',
        pageNumber: number = 0,
        pageSize: number = 10
    ): Observable<User[]> {
        return this.httpClient.get<User[]>('/admin/users/list', {
            params: new HttpParams()
                .set('filter', filter)
                .set('orderBy', orderBy)
                .set('sort', orderDirection.toUpperCase())
                .set('skip', pageNumber.toString())
                .set('take', pageSize.toString())
        });
    }

    public getUser(id: number): Observable<User> {
        return this.httpClient.get<UserResponse>('/admin/users/' + id).pipe(
            map((user) => {
                if (user.profile) {
                    return {
                        ...user,
                        lastLoginDate: new Date(user.lastLoginDate),
                        birthDate: new Date(user.profile.birthDate),
                    };
                }

                return user;
            })
        );
    }

    public editUser(data: UpdateProfileByAdminType): Observable<null> {
        return this.httpClient.put<null>('/admin/users', data);
    }

    public postChangePassword(data: { userId: number; newPassword: string }): Observable<any> {
        return this.httpClient.post<any>('/admin/users/change-password', data);
    }

    public getAccounts(userId: number): Observable<any> {
        return this.httpClient.get<any>('/admin/users/accounts/' + userId);
    }

    public getActivity(userId: number): Observable<any> {
        return this.httpClient.get<any>('/admin/users/activities/' + userId);
    }

    public getSessions(userId: number): Observable<any> {
        return this.httpClient.get<any>('/admin/users/security/sessions/' + userId);
    }

    public getDevices(userId: number): Observable<any> {
        return this.httpClient.get<any>('/admin/users/security/devices/' + userId);
    }

    public getBankAccounts(userId: number): Observable<any> {
        return this.httpClient.get<any>('/admin/users/bank/accounts/' + userId);
    }

    public editBankAccount(data: BankAccount): Observable<any> {
        return this.httpClient.put<any>('/admin/users/bank/account', data);
    }

    public deleteBankAccount(id: number): Observable<any> {
        return this.httpClient.delete<any>('/admin/users/bank/accounts/' + id);
    }

    public getCards(userId: number): Observable<any> {
        return this.httpClient.get<any>('/admin/users/cards/' + userId);
    }

    public getReferrals(userId: number): Observable<any> {
        return this.httpClient.get<any>('/admin/users/referrals/' + userId);
    }

    public postUserDisabledStatus(userId: number, disable: boolean): Observable<any> {
        return this.httpClient.post<any>('/admin/users/disable', {userId, disable});
    }

    public postUserDetainedStatus(detainData: { userId: number; detain: boolean; reason?: string }): Observable<any> {
        return this.httpClient.post<any>('/admin/users/detain', detainData);
    }

    public getUserRoles(userId: number): Observable<Array<UserRoleDto>> {
        return this.httpClient.get<Array<UserRoleDto>>('/admin/permissions/' + userId);
    }

    public postUserRoles(data: { userId: number; roles: any[] }): Observable<any> {
        return this.httpClient.post<any>('/admin/permissions', data);
    }

    public getUsersBalances(currencyCode: string, paginator: MatPaginator, sort: MatSort): Observable<any> {
        return this.httpClient.get<any>('/admin/users/balances/' + currencyCode, {
            params: new HttpParams()
                .set('filter', '')
                .set('orderBy', sort.active || 'balance')
                .set('sort', (sort.direction || 'DESC').toUpperCase())
                .set('skip', String(paginator.pageIndex || 0))
                .set('take', String(paginator.pageSize || 10))
        });
    }

    public getUserReport(type: string, profile: string, role: string, filter: string = '', orderBy: string = '', orderDirection: string = 'ASC') {
        const params = new HttpParams()
            .set('filter', filter)
            .set('orderBy', orderBy)
            .set('sort', orderDirection.toUpperCase());
        return this.httpClient.get(`/admin/users/reports/${type}/${profile}/${role}`, {responseType: 'blob', params});
    }

    public updateAccountFunds(data: { accountId: number; amount: number }): Observable<any> {
        return this.httpClient.post<any>('/admin/accounts/deposit/trust', data);
    }

    public topUpAccountFunds(data: { accountId: number; amount: number }): Observable<any> {
        return this.httpClient.post<any>('/admin/accounts/topup', data);
    }

    public getUserTurnovers(userId: number): Observable<any> {
        return this.httpClient.get<any>(`/admin/users/turnover/${userId}`);
    }

    public postRiskGroup(data: { userId: number; code: string; isFixed: boolean }): Observable<null> {
        return this.httpClient.post<null>('/admin/users/riskgroup', data);
    }

    public getTxMonitoringUser(userId: number): Observable<TransactionMonitoring> {
        return this.httpClient.get<TransactionMonitoring>(`/admin/tx-monitoring/user/${userId}`);
    }

    public postTxMonitoringUser(data: { userId: number; monitored: boolean }): Observable<TransactionMonitoring> {
        return this.httpClient.post<TransactionMonitoring>('/admin/tx-monitoring/user', data);
    }

    public postChangeEmail(data: { userId: number; email: string }): Observable<any> {
        return this.httpClient.post<any>('/admin/users/email/main', data);
    }

    public postChangeRecoveryEmail(data: { userId: number; email: string }): Observable<any> {
        return this.httpClient.post<any>('/admin/users/email/recovery', data);
    }

    public getTFAQrCode(): Observable<{ qrCodeImage: string }> {
        return this.httpClient.get<{ qrCodeImage: string }>('/auth/2fa/generate');
    }

    public disableTFA( authenticatorCode: string ): Observable<null> {
        return this.httpClient.post<null>('/auth/2fa/disable', {authenticatorCode});
    }

    public enableTFA(authenticatorCode: string): Observable<null> {
        return this.httpClient.post<null>('/auth/2fa/enable', {authenticatorCode});
    }
}
